import { parseUkPhoneNumber } from './phone';
import { getApiUrl } from '../utils';
import { trackEvent } from './analytics';

export interface CheckDuplicateParams {
  phone: string;
  email: string;
  device_id: string;
  service_id: string;
  partner_id: string;
  force_new?: boolean;
}

export interface CheckDuplicateResult {
  isDuplicate: boolean;
  existingJobId?: string;
  existingQuoteId?: string;
  existingAccessToken?: string;
  accessToken?: string;
  existingQuote?: PublicQuotePayload;
  quote?: PublicQuotePayload;
  reason?: string;
}

export interface PublicQuoteAddress {
  addressLine1?: string;
  addressLine2?: string;
  townCity?: string;
  county?: string;
  latitude?: string | number;
  longitude?: string | number;
  formattedAddress?: string;
  placeId?: string;
}

export interface PublicQuotePartnerSummary {
  partnerId?: string;
  companyName?: string;
  companyType?: string;
  pricingStyle?: string;
  calloutLabour?: number;
  calloutOnly?: number;
  hourlyRate?: number;
  commission?: number;
  latency?: string;
  phoneNumber?: string;
  email?: string;
  [key: string]: unknown;
}

export interface PublicQuotePayload {
  quoteId: string;
  status?: string;
  jobStatus?: string;
  appointmentDate?: string | null;
  serviceId?: string;
  serviceName?: string;
  applianceBrand?: string;
  faultDescription?: string;
  customerName?: string;
  firstName?: string;
  lastName?: string;
  customerEmail?: string;
  customerPhone?: string;
  postcode?: string;
  postcodeId?: string;
  address?: PublicQuoteAddress;
  pricingStyle?: string;
  calloutLabour?: number;
  calloutOnly?: number;
  hourlyRate?: number;
  companyName?: string;
  companyType?: string;
  commission?: number;
  latency?: string;
  partnerDetails?: PublicQuotePartnerSummary | null;
}

export class PublicQuoteApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.name = 'PublicQuoteApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface PublicQuoteDraftRequest {
  quoteId?: string;
  accessToken?: string;
  serviceId: string;
  serviceName: string;
  applianceBrand?: string;
  faultDescription?: string;
  gclid?: string;
  msclkid?: string;
  fbclid?: string;
  customerRef?: string;
  source?: string;
}

interface PublicQuoteDraftResponse {
  quoteId: string;
  accessToken: string;
  quote: PublicQuotePayload;
}

interface MatchPartnerRequest {
  quoteId: string;
  accessToken: string;
  serviceId: string;
  serviceName: string;
  postcode: string;
  addressLine1?: string;
  addressLine2?: string;
  townCity?: string;
  county?: string;
  formattedAddress?: string;
  placeId?: string;
  latitude?: number | string;
  longitude?: number | string;
}

interface MatchPartnerResponse {
  matched: boolean;
  quote: PublicQuotePayload;
  partner: PublicQuotePartnerSummary | null;
}

interface CustomerDetailsRequest {
  quoteId: string;
  accessToken: string;
  name: string;
  phone: string;
  email?: string;
}

interface QuoteResponse {
  quote?: PublicQuotePayload;
}

interface SelectDateRequest {
  quoteId: string;
  accessToken: string;
  appointmentDate: string;
}

interface ConfirmBookingRequest {
  quoteId: string;
  accessToken: string;
  addressLine1: string;
  addressLine2?: string;
  townCity: string;
  county?: string;
}

interface NoEngineerNotifyRequest {
  quoteId: string;
  accessToken: string;
  email: string;
}

interface JobPreparationRequest {
  quoteId: string;
  accessToken: string;
  parkingRestrictions?: string;
  specialInstructions?: string;
  occupancyStatus?: string;
  modelNumber?: string;
  faultDescription?: string;
  aiSummary?: string;
}

function getErrorCode(payload: any): string | undefined {
  return payload?.code || payload?.error?.code;
}

function getErrorMessage(payload: any, fallback: string): string {
  return payload?.message || payload?.error?.message || fallback;
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(getApiUrl(path), init);
  const contentType = response.headers.get('content-type') || '';
  const payload = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    throw new PublicQuoteApiError(
      typeof payload === 'string' ? payload : getErrorMessage(payload, `Request failed with ${response.status}`),
      response.status,
      typeof payload === 'string' ? undefined : getErrorCode(payload),
      payload,
    );
  }

  return payload as T;
}

function getBuildTelemetryMetadata() {
  const viteEnv = import.meta.env as Record<string, string | boolean | undefined>;
  const runtimeEnv =
    typeof window !== 'undefined'
      ? ((window as any).__FAST_REPAIR_ENV__ as Record<string, string | undefined> | undefined)
      : undefined;

  const read = (key: string): string | undefined => {
    const runtimeValue = runtimeEnv?.[key];
    if (typeof runtimeValue === 'string' && runtimeValue.length > 0) return runtimeValue;
    const viteValue = viteEnv[key];
    return typeof viteValue === 'string' && viteValue.length > 0 ? viteValue : undefined;
  };

  return {
    build_version: read('VITE_BUILD_VERSION') || read('VITE_APP_VERSION') || 'unknown',
    build_revision: read('VITE_BUILD_REVISION') || read('VITE_GIT_SHA') || 'unknown',
  };
}

function getTimerNow() {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

type QuoteApiStage = 'draft' | 'match_partner' | 'customer_details';

function trackQuoteApiEvent(
  stage: QuoteApiStage,
  status: 'start' | 'end' | 'failure',
  details: { quoteId?: string; durationMs?: number; errorCode?: string; httpStatus?: number },
) {
  trackEvent(`quote_api_${stage}_${status}`, {
    quote_id: details.quoteId || 'unknown',
    ...getBuildTelemetryMetadata(),
    ...(details.durationMs === undefined ? {} : { duration_ms: Math.round(details.durationMs) }),
    ...(details.errorCode ? { error_code: details.errorCode } : {}),
    ...(details.httpStatus === undefined ? {} : { http_status: details.httpStatus }),
  });
}

export async function createOrUpdatePublicQuoteDraft(
  request: PublicQuoteDraftRequest,
): Promise<PublicQuoteDraftResponse> {
  const startedAt = getTimerNow();
  trackQuoteApiEvent('draft', 'start', { quoteId: request.quoteId || 'new' });
  try {
    const response = await requestJson<PublicQuoteDraftResponse>('/api/public/quote/draft', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    trackQuoteApiEvent('draft', 'end', {
      quoteId: response.quoteId || request.quoteId || 'new',
      durationMs: getTimerNow() - startedAt,
    });
    return response;
  } catch (error) {
    const apiError = error instanceof PublicQuoteApiError ? error : undefined;
    trackQuoteApiEvent('draft', 'failure', {
      quoteId: request.quoteId || 'new',
      durationMs: getTimerNow() - startedAt,
      errorCode: apiError?.code,
      httpStatus: apiError?.status,
    });
    throw error;
  }
}

export async function matchPublicQuotePartner(
  request: MatchPartnerRequest,
): Promise<MatchPartnerResponse> {
  const startedAt = getTimerNow();
  trackQuoteApiEvent('match_partner', 'start', { quoteId: request.quoteId });
  try {
    const response = await requestJson<MatchPartnerResponse>('/api/public/quote/match-partner', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    trackQuoteApiEvent('match_partner', 'end', {
      quoteId: request.quoteId,
      durationMs: getTimerNow() - startedAt,
    });
    return response;
  } catch (error) {
    const apiError = error instanceof PublicQuoteApiError ? error : undefined;
    trackQuoteApiEvent('match_partner', 'failure', {
      quoteId: request.quoteId,
      durationMs: getTimerNow() - startedAt,
      errorCode: apiError?.code,
      httpStatus: apiError?.status,
    });
    throw error;
  }
}

export async function updatePublicQuoteCustomerDetails(
  request: CustomerDetailsRequest,
): Promise<QuoteResponse> {
  const startedAt = getTimerNow();
  trackQuoteApiEvent('customer_details', 'start', { quoteId: request.quoteId });
  try {
    const response = await requestJson<QuoteResponse>(`/api/public/quote/${encodeURIComponent(request.quoteId)}/customer-details`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        accessToken: request.accessToken,
        name: request.name,
        phone: request.phone,
        email: request.email || '',
      }),
    });
    trackQuoteApiEvent('customer_details', 'end', {
      quoteId: request.quoteId,
      durationMs: getTimerNow() - startedAt,
    });
    return response;
  } catch (error) {
    const apiError = error instanceof PublicQuoteApiError ? error : undefined;
    trackQuoteApiEvent('customer_details', 'failure', {
      quoteId: request.quoteId,
      durationMs: getTimerNow() - startedAt,
      errorCode: apiError?.code,
      httpStatus: apiError?.status,
    });
    throw error;
  }
}

export async function getPublicQuote(params: {
  quoteId: string;
  accessToken: string;
}): Promise<QuoteResponse> {
  const query = new URLSearchParams({ accessToken: params.accessToken });
  return requestJson<QuoteResponse>(`/api/public/quote/${encodeURIComponent(params.quoteId)}?${query.toString()}`);
}

export async function selectPublicQuoteDate(
  request: SelectDateRequest,
): Promise<QuoteResponse> {
  return requestJson<QuoteResponse>(`/api/public/quote/${encodeURIComponent(request.quoteId)}/select-date`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      accessToken: request.accessToken,
      appointmentDate: request.appointmentDate,
    }),
  });
}

export async function confirmPublicQuoteBooking(
  request: ConfirmBookingRequest,
): Promise<QuoteResponse> {
  return requestJson<QuoteResponse>(`/api/public/quote/${encodeURIComponent(request.quoteId)}/confirm-booking`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      accessToken: request.accessToken,
      addressLine1: request.addressLine1,
      addressLine2: request.addressLine2 || '',
      townCity: request.townCity,
      county: request.county || '',
    }),
  });
}

export async function notifyPublicQuoteNoEngineer(
  request: NoEngineerNotifyRequest,
): Promise<QuoteResponse> {
  return requestJson<QuoteResponse>(`/api/public/quote/${encodeURIComponent(request.quoteId)}/no-engineer-notify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      accessToken: request.accessToken,
      email: request.email,
    }),
  });
}

export async function updatePublicQuoteJobPreparation(
  request: JobPreparationRequest,
): Promise<QuoteResponse> {
  return requestJson<QuoteResponse>(`/api/public/quote/${encodeURIComponent(request.quoteId)}/job-preparation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      accessToken: request.accessToken,
      parkingRestrictions: request.parkingRestrictions || '',
      specialInstructions: request.specialInstructions || '',
      occupancyStatus: request.occupancyStatus || '',
      modelNumber: request.modelNumber || '',
      faultDescription: request.faultDescription || '',
      aiSummary: request.aiSummary || '',
    }),
  });
}

/**
 * Asks the server whether this submission would be a duplicate of an already-open
 * job for the same customer + service + partner within the last 30 days.
 */
export async function checkDuplicate(params: CheckDuplicateParams): Promise<CheckDuplicateResult> {
  const response = await fetch(getApiUrl('/api/jobs/check-duplicate'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    console.warn('[checkDuplicate] endpoint returned', response.status);
    return { isDuplicate: false };
  }

  return response.json() as Promise<CheckDuplicateResult>;
}

export const api = {
  async validatePhone(query: string) {
    const parsedPhone = parseUkPhoneNumber(query);
    return {
      result: {
        valid: Boolean(parsedPhone),
        number: parsedPhone?.e164 ?? null,
      },
    };
  },
};
